import { useMemo } from 'react';
import { DataGridPremium, GridColDef, GridRenderCellParams, GridValueGetterParams, useGridApiRef } from '@mui/x-data-grid-premium';
import { IconButton, SvgIcon, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import { Status } from '../../../hooks/useFetch';
import { ReactComponent as EditIcon } from '../../../icons/edit.svg';
import AudioFileStatusChip, { getAudioFileStatus } from './AudioFileStatusChip';
import { AudioFileCategory, AudioFileItem } from '@aviation/cabinapp-cabincast-sdk';
import { formatDuration } from './AudioFile';

export interface IAudioFilesGridProps {
    category?: AudioFileCategory;
    search?: string;
    data?: AudioFileItem[];
    status?: Status;
    onEdit?: (file: AudioFileItem) => void;
}

const formatDate = (value?: string) => {
    if (!value) return '';
    const date = dayjs(value, 'YYYY-MM-DD');
    return date.isValid() ? date.toDate().toLocaleDateString() : value;
};

const formatDateTime = (value?: string) => {
    if (!value) return '';
    const date = dayjs(value);
    return date.isValid() ? date.toDate().toLocaleString() : value;
};

const capitalize = (value?: string) => value ? value.charAt(0).toUpperCase() + value.slice(1) : '';

function AudioFilesGrid(props: IAudioFilesGridProps) {
    const { category, search, data = [], status, onEdit } = props;
    const apiRef = useGridApiRef();

    const rows = useMemo(() => {
        return data
            .filter(o => category === undefined || o.Category === category)
            .map(o => ({ ...o, id: o.Id }))
            .sort((a, b) => (a.Title ?? '').localeCompare(b.Title ?? ''));
    }, [data, category]);

    const columns: GridColDef[] = [
        {
            field: 'actions', headerName: '', width: 55, sortable: false, filterable: false, disableColumnMenu: true,
            renderCell: (params: GridRenderCellParams) => (
                <Tooltip title='Edit'>
                    <IconButton color='primary' onClick={() => onEdit?.(params.row)}>
                        <SvgIcon component={EditIcon} inheritViewBox />
                    </IconButton>
                </Tooltip>
            )
        },
        {
            field: 'Enabled', headerName: 'Status', width: 130,
            valueGetter: (params: GridValueGetterParams) => getAudioFileStatus(params.row),
            renderCell: (params: GridRenderCellParams) => <AudioFileStatusChip file={params.row} />
        },
        { field: 'Category', headerName: 'Category', width: 110, valueGetter: (params: GridValueGetterParams) => capitalize(params.row.Category) },
        { field: 'Title', headerName: 'Title', minWidth: 200, flex: 1 },
        {
            field: 'AvgDuration', headerName: 'Duration', width: 90,
            valueFormatter: (params) => formatDuration(params.value)
        },
        {
            field: 'Languages', headerName: 'Languages', width: 140,
            valueGetter: (params: GridValueGetterParams) => (params.row.Languages ?? []).join(', ')
        },
        { field: 'ValidFrom', headerName: 'Valid from', width: 110, valueFormatter: (params) => formatDate(params.value) },
        { field: 'ValidTo', headerName: 'Valid to', width: 110, valueFormatter: (params) => formatDate(params.value) },
        { field: 'UpdatedOn', headerName: 'Modified on', width: 160, valueFormatter: (params) => formatDateTime(params.value) },
        { field: 'UpdatedBy', headerName: 'Modified by', minWidth: 180 },
    ];

    return (
        <DataGridPremium
            apiRef={apiRef}
            autoHeight
            rows={rows}
            columns={columns}
            pagination
            disableRowSelectionOnClick
            getRowClassName={(params) => params.indexRelativeToCurrentPage % 2 === 1 ? 'tui-grid-alternate-row' : ''}
            loading={status === Status.Fetching || status === Status.Idle}
            filterModel={{ items: [], quickFilterValues: (search ?? '').split(' ').filter(Boolean) }}
        />
    );
}

export default AudioFilesGrid;
